<template>
  <basic-dialog :show="show" @close="$emit('close')" maxWidth="90%">
    <template v-slot:title>Changelogs</template>
    <v-data-table :items="items" :headers="headers" loading="$apollo.loading">
      <template
        v-for="colValue in dateColumnHeaderValues"
        v-slot:[`item.${colValue}`]="{ item }"
      >
        <span :key="colValue">{{
          getValueAtPath(item, colValue) | formatDate
        }}</span>
      </template>
      <template v-slot:[`item.itemAtChangeTime.notInUse`]="{ item }">
        <v-checkbox
          v-model="item.itemAtChangeTime.notInUse"
          disabled
        ></v-checkbox>
      </template>
      <template v-slot:[`item.createdAt`]="{ item }">
        {{ item.createdAt | formatDateTime }}
      </template>
    </v-data-table>
    <v-row v-if="debug">
      <v-col>
        <code>ownerId: {{ ownerId }}</code>
        <code>owner: {{ owner }}</code>
        <code>headers: {{ headers }}</code>
        <code>items: {{ items }}</code>
        <code>changelogs: {{ changelogs }}</code>
      </v-col>
    </v-row>
  </basic-dialog>
</template>

<script>
import gql from "graphql-tag";
import BasicDialog from "./BasicDialog.vue";
import { removeGQLTypename, sortByDate } from "../../../helpers";
const CHANGELOGS_QUERY = gql`
  query itemChangeLogs($itemId: ID) {
    changelogs(
      orderBy: { fieldName: "createdAt", direction: DESC }
      filter: { itemId: $itemId }
    ) {
      totalCount
      edges {
        node {
          id
          createdAt
          user {
            id
            name
          }
          itemAtChangeTime {
            ... on Markup {
              groupName
              groupId
              department
              destinationName
              destinationId
              validFrom
              validTo
              useDateRange
              round
              activity
              accommodation
              travel
              transport
              package
              other
              tagsList
              notInUse
              historicalOnly
              itineraryCreatedFrom
              itineraryCreatedTo
            }
            ... on Commission {
              groupId
              serviceType
              agentGroupName
              department
              commissionPercent
              adultAmount
              childAmount
              notInUse
            }
            ... on CircuitDiscount {
              validFrom
              validTo
              bookBy
              bookAfter
              description
              days
              minStay
              maxStay
              minSuppliers
              maxSuppliers
              maxStay
              percent
              percentCost
              free
              groupId
              agentGroupName
              tagsList
            }
          }
        }
      }
    }
  }
`;
export default {
  name: "changelogs-dialog",
  props: {
    ownerId: {
      type: String,
      default: () => "",
    },
    show: {
      type: Boolean,
      default: () => false,
    },
  },

  components: { BasicDialog },

  data() {
    return {
      debug: false,
      changelogHeaders: [
        {
          text: "Date",
          value: "createdAt",
          name: "createdAt",
        },
        {
          text: "User",
          value: "user.name",
          name: "user",
        },
      ],
      commissionHeaders: [
        {
          text: "Service Type",
          value: "itemAtChangeTime.serviceType",
          name: "serviceType",
        },
        {
          text: "Agent Group",
          value: "itemAtChangeTime.agentGroupName",
          name: "agentGroupName",
        },
        {
          text: "Department",
          value: "itemAtChangeTime.department",
          name: "department",
        },
        {
          text: "Commission %",
          value: "itemAtChangeTime.commissionPercent",
          name: "commissionPercent",
        },
        {
          text: "Adult Amount",
          value: "itemAtChangeTime.adultAmount",
          name: "adultAmount",
        },
        {
          text: "Child Amount",
          value: "itemAtChangeTime.childAmount",
          name: "childAmount",
        },
        {
          text: "Not In Use",
          value: "itemAtChangeTime.notInUse",
          name: "notInUse",
        },
      ],
      markupHeaders: [
        {
          text: "Agent Group",
          value: "itemAtChangeTime.groupName",
          name: "groupName",
        },
        {
          text: "From",
          value: "itemAtChangeTime.validFrom",
          name: "validFrom",
          sort: sortByDate,
        },
        {
          text: "To",
          value: "itemAtChangeTime.validTo",
          name: "validTo",
          sort: sortByDate,
        },
        {
          text: "Iti Created From",
          value: "itemAtChangeTime.itineraryCreatedFrom",
          name: "itineraryCreatedFrom",
          sort: sortByDate,
        },
        {
          text: "Iti Created To",
          value: "itemAtChangeTime.itineraryCreatedTo",
          name: "itineraryCreatedTo",
          sort: sortByDate,
        },
        {
          text: "Department",
          value: "itemAtChangeTime.department",
          name: "department",
        },
        {
          text: "Destination",
          value: "itemAtChangeTime.destinationName",
          name: "destinationName",
        },
        {
          text: "Accommodation %",
          value: "itemAtChangeTime.accommodation",
          name: "accommodation",
        },
        {
          text: "Activity %",
          value: "itemAtChangeTime.activity",
          name: "activity",
        },
        {
          text: "Transport %",
          value: "itemAtChangeTime.transport",
          name: "transport",
        },
        {
          text: "Travel %",
          value: "itemAtChangeTime.travel",
          name: "travel",
        },
        {
          text: "Other %",
          value: "itemAtChangeTime.other",
          name: "other",
        },
        {
          text: "Package %",
          value: "itemAtChangeTime.package",
          name: "package",
        },
        {
          text: "Tags",
          value: "itemAtChangeTime.tagsList",
          name: "tagsList",
        },
        {
          text: "Not In Use",
          value: "itemAtChangeTime.notInUse",
          name: "notInUse",
        },
        {
          text: "Historical Only",
          value: "itemAtChangeTime.historicalOnly",
          name: "historicalOnly",
        },
      ],
      circuitDiscountHeaders: [
        {
          text: "Description",
          value: "itemAtChangeTime.description",
          name: "description",
        },
        {
          text: "Tags",
          value: "itemAtChangeTime.tagsList",
          name: "tagsList",
        },
        {
          text: "Min Suppliers",
          value: "itemAtChangeTime.minSuppliers",
          name: "minSuppliers",
        },
        {
          text: "Max Suppliers",
          value: "itemAtChangeTime.maxSuppliers",
          name: "maxSuppliers",
        },
        {
          text: "Min Stay",
          value: "itemAtChangeTime.minStay",
          name: "minStay",
        },
        {
          text: "Max Stay",
          value: "itemAtChangeTime.maxStay",
          name: "maxStay",
        },
        {
          text: "Cost %",
          value: "itemAtChangeTime.percentCost",
          name: "percentCost",
        },
        {
          text: "Sell %",
          value: "itemAtChangeTime.percent",
          name: "percent",
        },
        /*{
          text: "Free",
          value: "free",
          name: "free",
        },*/
        {
          text: "Iti Created From",
          value: "itemAtChangeTime.bookAfter",
          name: "bookAfter",
        },
        {
          text: "Iti Created To",
          value: "itemAtChangeTime.bookBy",
          name: "bookBy",
        },
        {
          text: "Days",
          value: "itemAtChangeTime.days",
          name: "days",
        },
        {
          text: "Valid From",
          value: "itemAtChangeTime.validFrom",
          name: "validFrom",
        },
        {
          text: "Valid To",
          value: "itemAtChangeTime.validTo",
          name: "validTo",
        },
        {
          text: "Agent Group",
          value: "itemAtChangeTime.agentGroupName",
          name: "agentGroupName",
        },
      ],
      dateFields: [
        "validFrom",
        "validTo",
        "bookAfter",
        "bookBy",
        "itineraryCreatedFrom",
        "itineraryCreatedTo",
        "createdAt",
        "updatedAt",
      ],
    };
  },

  computed: {
    items() {
      return this.changelogs && this.changelogs.edges
        ? this.changelogs.edges.map(removeGQLTypename)
        : [];
    },
    owner() {
      return this.ownerId != "" ? atob(this.ownerId).split("-")[0] : "";
    },
    headers() {
      switch (this.owner) {
        case "Commission":
          return [...this.changelogHeaders, ...this.commissionHeaders];
        case "Markup":
          return [...this.changelogHeaders, ...this.markupHeaders];
        case "CircuitDiscount":
          return [...this.changelogHeaders, ...this.circuitDiscountHeaders];
        default:
          return [...this.changelogHeaders];
      }
      //return [...this.changelogHeaders];
    },
    /** Header `value` strings for date columns — must match v-data-table slot names (`item.${value}`). */
    dateColumnHeaderValues() {
      const suffixes = new Set(this.dateFields);
      return this.headers
        .filter((h) => suffixes.has(h.value.split(".").pop()))
        .map((h) => h.value);
    },
  },

  methods: {
    getValueAtPath(obj, path) {
      if (obj == null || path == null) return null;
      return path
        .split(".")
        .reduce((acc, key) => (acc != null ? acc[key] : undefined), obj);
    },
  },

  apollo: {
    changelogs: {
      query: CHANGELOGS_QUERY,
      variables() {
        return {
          itemId: this.ownerId,
        };
      },
      fetchPolicy: "network-only",
      skip() {
        return this.ownerId == "" || this.ownerId == null || this.show == false;
      },
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/shared/ChangelogsDialog.vue